import {commitMutation, graphql} from 'react-relay'
import type {Environment} from 'relay-runtime'

import type {CreateIssueFieldValueInput} from './__generated__/createIssueFieldValueMutation.graphql'
import {ISSUE_FIELD_VALUES_PAGE_SIZE} from './constants'
import {getFieldValueType, replaceFieldValueInCollection, updateNodeValues} from './helpers'

export function commitCreateIssueFieldValueMutation({
  environment,
  input,
  onCompleted,
  onError,
}: {
  environment: Environment
  input: CreateIssueFieldValueInput
  onCompleted?: () => void
  onError?: (error?: Error) => void
}) {
  return commitMutation(environment, {
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    mutation: graphql`
      mutation createIssueFieldValueMutation($input: CreateIssueFieldValueInput!) {
        createIssueFieldValue(input: $input) {
          issueFieldValue {
            ... on IssueFieldTextValue {
              id
              value
            }
            ... on IssueFieldSingleSelectValue {
              id
              name
              color
              description
            }
            ... on IssueFieldDateValue {
              id
              value
            }
            ... on IssueFieldNumberValue {
              id
              numberValue: value
            }
          }
          issue {
            id
          }
        }
      }
    `,
    variables: {
      input,
    },
    onCompleted: () => {
      onCompleted?.()
    },
    onError: error => onError?.(error),
    optimisticUpdater: store => {
      const issue = store.get(input.issueId)
      if (!issue) return
      const fieldInput = input.issueField
      const fieldValues = issue.getLinkedRecord('issueFieldValues', {first: ISSUE_FIELD_VALUES_PAGE_SIZE})
      const nodes = fieldValues?.getLinkedRecords('nodes') || []

      // Create new optimistic node
      const type = getFieldValueType(fieldInput)
      const node = store.create(`client:newFieldValue:${fieldInput.fieldId}`, type)
      node.setValue(`client:newFieldValue:${fieldInput.fieldId}`, 'id')
      const fieldRecord = store.get(fieldInput.fieldId)
      if (fieldRecord) node.setLinkedRecord(fieldRecord, 'field')

      updateNodeValues(store, node, fieldInput)

      // Append the new node to the array
      nodes.push(node)

      if (fieldValues) {
        fieldValues.setLinkedRecords(nodes, 'nodes')
      }
    },
    updater: store => {
      const issue = store.get(input.issueId)
      const fieldValues = issue?.getLinkedRecord('issueFieldValues', {first: ISSUE_FIELD_VALUES_PAGE_SIZE})
      const payload = store.getRootField('createIssueFieldValue')
      const newValue = payload?.getLinkedRecord('issueFieldValue')

      if (!issue || !newValue || !fieldValues) return

      const fieldInput = input.issueField

      // Ensure field relation is set on new value
      if (!newValue.getLinkedRecord('field')) {
        const fieldRecord = store.get(fieldInput.fieldId)
        if (fieldRecord) newValue.setLinkedRecord(fieldRecord, 'field')
      }

      replaceFieldValueInCollection(fieldValues, fieldInput.fieldId, newValue)
    },
  })
}
